// Storage Access Framework (Android, via Capacitor) implementation of the
// folder-watching provider - the native counterpart to provider.js's File
// System Access API implementation. Implements the exact same 5-method
// contract (see provider.js's header comment), so useLocalAutoSync.js needs
// no changes beyond picking which provider to default to (see
// isNativeSyncEnvironment() below, used by useLocalAutoSync.js).
//
// Always runs in the normal, visible app WebView (MainActivity), which has
// the full Capacitor bridge, so every call here goes through the
// NativeFolderSync Capacitor plugin (registerPlugin below). This used to also
// run inside a headless WebView FolderSyncForegroundService spun up to
// resume folder auto-sync while the app was fully closed (a bare WebView with
// no Capacitor bridge, talking to a `window.cmHeadlessSyncBridge`
// @JavascriptInterface object instead) - that path (SyncWorkerPage.js's
// FolderSyncWorker) has been fully migrated onto the native WorkManager
// pipeline instead (see AutoSyncTrigger.kt), so this provider is never
// actually exercised headlessly anymore.
import { registerPlugin, Capacitor } from '@capacitor/core';
import { ScanCancelledError } from './provider';

const NativeFolderSync = registerPlugin('NativeFolderSync');

const DEFAULT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heic', 'heif'];

export function isNativeSyncEnvironment() {
  return Capacitor.isNativePlatform();
}

export const nativeFolderSyncProvider = {
  isSupported() {
    return isNativeSyncEnvironment();
  },

  async pickDirectory() {
    const res = await NativeFolderSync.pickDirectory();
    if (!res || res.cancelled || !res.treeUri) return null;
    // This surrogate (not a real FileSystemDirectoryHandle) is what gets
    // persisted via db.js's structured-clone IndexedDB write - plain
    // string fields clone without issue. `.name` must exist directly on it:
    // useLocalAutoSync.js reads `dirHandleRef.current.name`/`handle.name` in
    // several places assuming this exact shape.
    return { treeUri: res.treeUri, name: res.name || 'folder' };
  },

  async queryPermission(handle) {
    if (!handle?.treeUri) return 'denied';
    const res = await NativeFolderSync.queryPermission({ treeUri: handle.treeUri });
    return res?.state === 'granted' ? 'granted' : 'denied';
  },

  // SAF has no live re-prompt for an already-picked tree the way the File
  // System Access API's requestPermission can re-show a real consent UI -
  // once a persisted grant is gone (uninstall/resign, or the user manually
  // revoked it in Android settings), the only recovery is re-running
  // pickDirectory() via "Choose folder" again. This deliberately just
  // reports current state rather than pretending to re-prompt.
  async requestPermission(handle) {
    return nativeFolderSyncProvider.queryPermission(handle);
  },

  async listImageFiles(handle, { onProgress, signal } = {}) {
    if (!handle?.treeUri) return [];

    let cancelledLocally = false;
    const onAbort = () => { cancelledLocally = true; NativeFolderSync.cancelListImageFiles().catch(() => {}); };
    signal?.addEventListener('abort', onAbort);
    const progressListener = onProgress
      ? await NativeFolderSync.addListener('listImageFilesProgress', (e) => onProgress({ phase: 'scanning', completed: e.completed, total: null }))
      : null;
    try {
      const { files } = await NativeFolderSync.listImageFiles({ treeUri: handle.treeUri, extensions: DEFAULT_EXTENSIONS });
      if (signal?.aborted || cancelledLocally) throw new ScanCancelledError();
      return files.map((e) => ({
        relativePath: e.relativePath,
        getFile: () => nativeFolderSyncProvider.resolveFile(handle, e.relativePath),
      }));
    } catch (err) {
      if (signal?.aborted || cancelledLocally) throw new ScanCancelledError();
      throw err;
    } finally {
      signal?.removeEventListener('abort', onAbort);
      await progressListener?.remove();
    }
  },

  async resolveFile(handle, relativePath) {
    const res = await NativeFolderSync.resolveFile({ treeUri: handle.treeUri, relativePath });
    const fileUrl = Capacitor.convertFileSrc(res.path);
    try {
      const blobRes = await fetch(fileUrl);
      const blob = await blobRes.blob();
      return new File([blob], res.name, { type: res.mimeType || blob.type, lastModified: res.lastModified || Date.now() });
    } finally {
      // resolveFile's only reason to copy the original into app cache at all is
      // that the WebView needs a real file:// path to fetch() - once that fetch
      // is done (or failed) the bytes are already in the Blob above and the disk
      // copy is pure waste. Without this, every resolved photo's full-resolution
      // original piled up in cache/sync-resolve/ forever (confirmed on-device as
      // unbounded app storage growth during a 2700-photo auto-sync).
      NativeFolderSync.cleanupResolvedFile({ path: res.path }).catch(() => {});
    }
  },
};

// Native-only surface beyond the shared provider contract: owns the
// foreground service's lifecycle so it always mirrors useLocalAutoSync's own
// armed/un-armed state, and relays batch stats from a running sync into the
// service's persistent notification. Kept out of nativeFolderSyncProvider
// itself so that object stays a drop-in match for provider.js's exact
// 5-method interface.
export const nativeSyncService = {
  // `uploadLinkToken`/`identityName`/`identityEmail`/`identityPhone`/`sessionId`/
  // `canGroup`/`groupingMode` are upload-link-only (see useLocalAutoSync.js's
  // nativeTargetMeta) - undefined for the organiser flow. Persisted into
  // SyncPrefs.Target so AutoSyncTrigger.kt can act as this exact guest
  // submitter while the app is fully closed, the same native WorkManager path
  // organiser targets already use.
  async startForegroundSync({
    storageKey, treeUri, eventId, pollIntervalMs, batchThreshold,
    uploadLinkToken, identityName, identityEmail, identityPhone, sessionId, canGroup, groupingMode,
  }) {
    if (!Capacitor.isNativePlatform()) return;
    console.log('[AutoSync] nativeProvider.startForegroundSync: storageKey=%s eventId=%s batchThreshold=%s', storageKey, eventId, batchThreshold);
    await NativeFolderSync.startForegroundSync({
      storageKey, treeUri, eventId, pollIntervalMs, batchThreshold,
      uploadLinkToken, identityName, identityEmail, identityPhone, sessionId, canGroup, groupingMode,
    }).catch((err) => console.error('[AutoSync] nativeProvider.startForegroundSync failed:', storageKey, err?.message || err));
  },
  // Keeps AutoSyncCheckWorker's own native seen-set in sync with this hook's
  // authoritative JS-side seenKeys (IndexedDB) - see AutoSyncSeenStore's own
  // doc comment for why the closed-app fallback needs its own copy at all,
  // and why it must stay in sync with whatever JS itself already handled.
  async seedAutoSyncSeen(storageKey, relativePaths) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.seedAutoSyncSeen({ storageKey, relativePaths })
      .catch((err) => console.error('[AutoSync] nativeProvider.seedAutoSyncSeen failed:', storageKey, err?.message || err));
  },
  async markAutoSyncSeen(storageKey, relativePaths) {
    if (!Capacitor.isNativePlatform() || !relativePaths.length) return;
    await NativeFolderSync.markAutoSyncSeen({ storageKey, relativePaths })
      .catch((err) => console.error('[AutoSync] nativeProvider.markAutoSyncSeen failed:', storageKey, err?.message || err));
  },
  // Mirrors JS's pendingQueueRef down to native every time it changes - see
  // NativeFolderSyncPlugin.kt's setAutoSyncPending for why this is needed
  // (not just seen-tracking) to close a real gap.
  async setAutoSyncPending(storageKey, relativePaths) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.setAutoSyncPending({ storageKey, relativePaths })
      .catch((err) => console.error('[AutoSync] nativeProvider.setAutoSyncPending failed:', storageKey, err?.message || err));
  },
  // Reverse direction of setAutoSyncPending above: reads back whatever
  // AutoSyncCheckWorker (the closed-app WorkManager fallback) discovered on its
  // own while this JS engine wasn't running at all. Without this, a file that
  // worker found and queued natively - correctly reflected in the persistent
  // notification - stays permanently invisible to this hook's own pendingQueue/
  // IndexedDB and hence to its own threshold check, until its next full folder
  // re-scan happens to rediscover the same file as "new" from scratch. See
  // useLocalAutoSync.js's mount effect for the merge this feeds.
  async getAutoSyncPending(storageKey) {
    if (!Capacitor.isNativePlatform()) return [];
    const res = await NativeFolderSync.getAutoSyncPending({ storageKey }).catch(() => null);
    return res?.relativePaths || [];
  },
  // Companion to getAutoSyncPending above, closing the other half of the same gap: files
  // AutoSyncTrigger/AutoSyncCheckWorker has already fully uploaded (not just discovered) are
  // marked "seen" in native's AutoSyncSeenStore but never communicated back to this hook's
  // own seenKeysRef/IndexedDB. Without this, reopening the app mid- or post-processing makes
  // this hook's own doPoll scan treat those already-handled files as brand new again and
  // re-upload them a second time - confirmed on a real device. See useLocalAutoSync.js's
  // mount effect for the merge this feeds, used together with getAutoSyncPending.
  async getAutoSyncSeen(storageKey) {
    if (!Capacitor.isNativePlatform()) return [];
    const res = await NativeFolderSync.getAutoSyncSeen({ storageKey }).catch(() => null);
    return res?.relativePaths || [];
  },
  async stopForegroundSync(storageKey) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.stopForegroundSync({ storageKey })
      .catch((err) => console.error('[AutoSync] nativeProvider.stopForegroundSync failed:', storageKey, err?.message || err));
  },
  // Called once a batch resolves, so the service can update its one
  // persistent notification. `statusDetail`, if given, overrides the
  // notification's usual "N/M pending" text - see useLocalAutoSync.js's
  // reportNativeBatchResult for the full rationale.
  async reportBatchResult({ storageKey, pendingCount, lastResult, lastSyncAt, statusDetail = null }) {
    if (!Capacitor.isNativePlatform()) return;
    await NativeFolderSync.reportBatchResult({ storageKey, pendingCount, lastResult, lastSyncAt, statusDetail })
      .catch((err) => console.error('[AutoSync] nativeProvider.reportBatchResult failed:', storageKey, err?.message || err));
  },
  // Best-effort - resolves `{value: true}` immediately (no dialog) if already
  // exempt, otherwise shows Android's standard one-tap system dialog. Only
  // covers stock Android's Doze/App Standby restrictions - OEM-specific
  // background killers (MIUI's "Autostart" permission prominently among them)
  // have no equivalent public API and can only be granted by the user
  // manually, in that OEM's own Settings/Security app.
  async requestBatteryOptimizationExemption() {
    if (!Capacitor.isNativePlatform()) return { value: false };
    return NativeFolderSync.requestBatteryOptimizationExemption().catch(() => ({ value: false }));
  },
};

// Backs the plain (non-folder) "Choose local photos" multi-select picker on
// native - deliberately NOT part of nativeFolderSyncProvider's 5-method
// contract (that's a 1:1 mirror of provider.js's own File-System-Access-API
// shape, which has no equivalent "just pick some files" primitive). Returns
// bare {uri, name, size} descriptors, never resolving real bytes - the same
// "stay lazy" approach useManualFolderPersistence.js's toPlaceholderFile uses
// for a folder pick, here via NativeFolderSyncPlugin.pickFiles's persistable
// per-document URI grants instead of a folder treeUri. See CmUploadWorker.kt's
// resolveOriginalBytes "saf-docs" branch for where these URIs get resolved -
// lazily, one at a time, at actual upload time, straight from SAF.
export const nativeFilePicker = {
  supported() {
    return isNativeSyncEnvironment();
  },
  async pickFiles(extensions) {
    if (!isNativeSyncEnvironment()) return [];
    const res = await NativeFolderSync.pickFiles({ extensions: extensions || DEFAULT_EXTENSIONS });
    return (res?.files || []).map((f) => ({ uri: f.uri, name: f.name, size: f.size || 0 }));
  },
};
